import BaseApi, { PRIORITY } from '@lumapps/base-api';
import { QueryClient } from '@lumapps/base-api/react-query';
import { ServerListResponse } from '@lumapps/base-api/types';
import { createGetMultiBatcher } from '@lumapps/base-api/utils/createGetMultiBatcher';
import { getSupersetFieldsFromCache } from '@lumapps/base-api/utils/getSupersetFieldsFromCache';
import { CACHE_TYPE } from '@lumapps/cache';
import { hasFields } from '@lumapps/utils/object/hasFields';
import { ONE_MINUTE } from '@lumapps/utils/time/constants';
import type { Fields } from '@lumapps/utils/types/Fields';

import { MINIMAL_INSTANCE_FIELDS, MINIMAL_INSTANCE_FIELDS_ARRAY, SEARCH_INSTANCE_DEFAULT_PARAMS } from '../constants';
import type { Instance } from '../types';
import type {
    GetInstanceParams,
    ListInstanceParams,
    ListInstanceSiblingsParams,
    QueryInstanceOptions,
    QueryInstanceSearchOptions,
} from './types';

export const PATH = 'instance';

export const instanceApi = new BaseApi({ path: PATH });

export const listInstances = (params: ListInstanceParams) =>
    instanceApi.get<ServerListResponse<Instance>>('list', { params });

/**
 * Retrieves an instance from the server
 * @param params GetInstanceParams
 * @param ignoreCache whether the instance should be retrieved from cache or not.
 * @returns Promise<Instance>
 */
export const getInstance = (
    params: GetInstanceParams,
    retrieveFromCache = true,
    signal: AbortSignal | undefined = undefined,
) => {
    if (!retrieveFromCache) {
        return instanceApi.get<Instance>('get', { params, signal });
    }

    return instanceApi.getCacheFirst<Instance>('get', CACHE_TYPE.MEMORY, PRIORITY.HIGH, { params, signal });
};

export const listInstanceSiblings = async (params: ListInstanceSiblingsParams, signal: AbortSignal | undefined) => {
    const fields = `items(${params.fields}),more,callId,cursor`;
    const { data } = await instanceApi.get<ServerListResponse<Instance>>('siblings/list', {
        params: { ...params, fields },
        signal,
    });

    return data;
};

/** Search instances */
export async function searchInstances(
    { itemFields = MINIMAL_INSTANCE_FIELDS, ...params } = SEARCH_INSTANCE_DEFAULT_PARAMS,
    signal: AbortSignal | undefined = undefined,
) {
    const fields = `items(${itemFields}),more,callId,cursor`;
    const { data } = await instanceApi.post<ServerListResponse<Instance>>('search', { ...params, fields }, { signal });
    return data;
}

/**
 * Get instance batched.
 * Calling it multiple time within a render frame will issue a single API call to get the instances.
 */
export const getInstanceBatched = createGetMultiBatcher<Instance>({
    getId: 'id',
    async getMulti(ids, fieldsArray, signal) {
        const fields = fieldsArray?.join(',') || '';
        // Get a single instance
        if (ids.length === 1) {
            const { data } = await getInstance({ uid: ids[0], fields }, false, signal);
            return [data];
        }

        // Using `instance/search` to handle more IDs thanks to the POST method (instead of the GET method used in `instance/list` API)
        const { items } = await searchInstances(
            { ids, itemFields: fields, maxResults: ids.length, sortOrder: undefined, userFavoritesOnly: undefined },
            signal,
        );
        return items;
    },
});

export const saveInstance = (instance: Instance) => instanceApi.post<Instance>('/save', instance);

export const deleteInstances = (uid: string[]) => instanceApi.delete<Instance>('/deleteMulti', { params: { uid } });

export const instanceQueryKeys = {
    all: [PATH] as const,
    getWithoutFields: (id: string) => [...instanceQueryKeys.all, 'get', id] as const,
    get: (id: string, fields: Fields<Instance>) => [...instanceQueryKeys.getWithoutFields(id), fields] as const,
    search: (params: QueryInstanceSearchOptions) => [...instanceQueryKeys.all, 'search', params] as const,
    siblingsList: (params: ListInstanceSiblingsParams) => [...instanceQueryKeys.all, 'siblingsList', params] as const,
};

/** Get instance query */
export function getQueryInstance(
    queryClient: QueryClient,
    instance: Partial<Instance> | string | undefined = undefined,
    { fields: baseFields = MINIMAL_INSTANCE_FIELDS_ARRAY, enabled = true, ...options }: QueryInstanceOptions = {},
) {
    const id = typeof instance === 'string' ? instance : instance?.id || '';
    const initialData = hasFields(instance, baseFields) ? instance : undefined;

    const fields = initialData
        ? baseFields
        : // We try to re-use previously requested fields to avoid refetching when we might already have the data
          getSupersetFieldsFromCache(queryClient, instanceQueryKeys.get(id, baseFields));

    return {
        queryKey: instanceQueryKeys.get(id, fields),
        queryFn: ({ signal }: { signal?: AbortSignal }) => getInstanceBatched(id, fields, signal),
        staleTime: 5 * ONE_MINUTE,
        enabled: !!enabled && !!id,
        initialData,
        ...options,
    } as const;
}
